/**
 * Сравнение редакций карточки в ченджлоге: поля (заголовок, вид, тема, город) и построчный diff
 * текста (LCS по строкам). Удалено — красным с «−», добавлено — зелёным с «+»: цвет всегда со знаком.
 */
import { useMemo } from "react";

import type { QcContent } from "@/lib/qc";

type Line = { op: "=" | "-" | "+"; text: string };

export function diffLines(a: string, b: string): Line[] {
  const x = a.split("\n");
  const y = b.split("\n");
  const n = x.length;
  const m = y.length;
  const w = m + 1;
  const dp = new Int32Array((n + 1) * w);
  const at = (i: number, j: number) => dp[i * w + j] ?? 0;
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--) dp[i * w + j] = x[i] === y[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
  const out: Line[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (x[i] === y[j]) {
      out.push({ op: "=", text: x[i] ?? "" });
      i++;
      j++;
    } else if (at(i + 1, j) >= at(i, j + 1)) out.push({ op: "-", text: x[i++] ?? "" });
    else out.push({ op: "+", text: y[j++] ?? "" });
  }
  while (i < n) out.push({ op: "-", text: x[i++] ?? "" });
  while (j < m) out.push({ op: "+", text: y[j++] ?? "" });
  return out;
}

const KIND: Record<string, string> = { answer: "ответ", fact: "факт", process: "процесс", data_howto: "где взять данные" };

export function ProposalDiff({
  before,
  after,
  cityOf,
}: {
  before: QcContent | null;
  after: QcContent;
  cityOf?: (id: number | null) => string;
}) {
  const city = cityOf ?? ((id: number | null) => (id === null ? "все салоны" : `город ${id}`));
  const fields = before
    ? ([
        ["Заголовок", before.title, after.title],
        ["Вид", KIND[before.kind] ?? before.kind, KIND[after.kind] ?? after.kind],
        ["Тема", before.topic, after.topic],
        ["Город", city(before.location_id), city(after.location_id)],
      ] as const).filter(([, a, b]) => a !== b)
    : [];
  // LCS — не на каждый кадр (уровень микрофона в соседней форме перерисовывает пункт часто).
  const lines = useMemo(() => diffLines(before?.body ?? "", after.body), [before?.body, after.body]);
  return (
    <div className="flex flex-col gap-1.5 text-[13px]" data-annot="сравнение редакций">
      {!before && (
        <div className="text-fg-muted">
          Новая карточка «<b className="text-fg">{after.title}</b>» · {KIND[after.kind] ?? after.kind} · {after.topic} · {city(after.location_id)}
        </div>
      )}
      {fields.map(([name, a, b]) => (
        <div key={name}>
          {name}: <span className="bg-err-bg text-err line-through">{a}</span> → <span className="bg-ok-bg text-ok">{b}</span>
        </div>
      ))}
      <div className="overflow-x-auto rounded-[8px] border border-border leading-[19px]">
        {lines.map((l, i) => (
          <div
            key={i}
            className={`flex gap-2 whitespace-pre-wrap break-words px-2 ${l.op === "-" ? "bg-err-bg text-err" : l.op === "+" ? "bg-ok-bg text-ok" : "text-fg"}`}
          >
            <span className="w-3 flex-none select-none text-center">{l.op === "=" ? "" : l.op === "-" ? "−" : "+"}</span>
            <span className="min-w-0 flex-1">{l.text || " "}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
